import { Amplify } from "aws-amplify";
import {
  autoSignIn,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  resendSignUpCode,
  signIn,
  signOut as amplifySignOut,
  signUp,
} from "aws-amplify/auth";
import type { ConsoleConfig } from "./config";

/**
 * How the code the person was sent is used:
 * - `sign-up`: confirms a new account and signs in from the confirmation session.
 * - `sign-in`: answers the EMAIL_OTP challenge of an existing account.
 * - `confirm`: confirms an account whose sign-up was never finished; a sign-in code follows.
 */
export type CodeFlow = "sign-up" | "sign-in" | "confirm";

/** Cognito's sign-up confirmation codes have six digits; its passwordless sign-in codes have eight. */
export function codeLength(flow: CodeFlow): number {
  return flow === "sign-in" ? 8 : 6;
}

/** What happened after a code was entered. */
export type CodeResult = { signedIn: true } | { signedIn: false; flow: CodeFlow };

export type AuthErrorKind = "code" | "limit" | "unknown";

export function configureAuth(config: ConsoleConfig) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolClientId,
        loginWith: { email: true },
        signUpVerificationMethod: "code",
      },
    },
  });
}

/** Sends a code to the address: a sign-up code for a new address, a sign-in code for an existing account. */
export async function sendCode(email: string): Promise<CodeFlow> {
  try {
    await signUp({
      username: email,
      options: { userAttributes: { email }, autoSignIn: { authFlowType: "USER_AUTH" } },
    });
    return "sign-up";
  } catch (error) {
    if (errorName(error) !== "UsernameExistsException") throw error;
  }
  return sendSignInCode(email);
}

async function sendSignInCode(email: string): Promise<CodeFlow> {
  try {
    const { nextStep } = await signIn({
      username: email,
      options: { authFlowType: "USER_AUTH", preferredChallenge: "EMAIL_OTP" },
    });
    if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") return "sign-in";
    if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
      await resendSignUpCode({ username: email });
      return "confirm";
    }
    throw new Error(`Unexpected sign-in step ${nextStep.signInStep}.`);
  } catch (error) {
    if (errorName(error) !== "UserNotConfirmedException") throw error;
    await resendSignUpCode({ username: email });
    return "confirm";
  }
}

/** Uses the code the person entered. */
export async function submitCode(email: string, flow: CodeFlow, code: string): Promise<CodeResult> {
  if (flow === "sign-in") {
    const { isSignedIn, nextStep } = await confirmSignIn({ challengeResponse: code });
    if (isSignedIn) return { signedIn: true };
    throw new Error(`Unexpected sign-in step ${nextStep.signInStep}.`);
  }
  const { nextStep } = await confirmSignUp({ username: email, confirmationCode: code });
  if (flow === "sign-up" && nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
    const { isSignedIn } = await autoSignIn();
    if (isSignedIn) return { signedIn: true };
  }
  // The confirmation session is not available (the page was reloaded), so sign in with a new code.
  return { signedIn: false, flow: await sendSignInCode(email) };
}

/** Sends a new code for the same flow. */
export async function resendCode(email: string, flow: CodeFlow): Promise<CodeFlow> {
  if (flow === "sign-in") return sendSignInCode(email);
  await resendSignUpCode({ username: email });
  return flow;
}

/** The signed-in person's email address, refreshing the tokens if needed, or undefined when signed out. */
export async function currentEmail(): Promise<string | undefined> {
  try {
    const { tokens } = await fetchAuthSession();
    const email = tokens?.idToken?.payload.email;
    return typeof email === "string" ? email : undefined;
  } catch {
    return undefined;
  }
}

/** The access token for API calls, refreshed by Amplify when it has expired. */
export async function accessToken(): Promise<string | undefined> {
  const { tokens } = await fetchAuthSession();
  return tokens?.accessToken.toString();
}

/** Revokes the refresh token and clears the session from this browser. */
export async function signOut(): Promise<void> {
  await amplifySignOut();
}

export function classifyAuthError(error: unknown): AuthErrorKind {
  const name = errorName(error);
  const message = error instanceof Error ? error.message : "";
  if (name === "CodeMismatchException" || name === "ExpiredCodeException") return "code";
  // An expired or used-up sign-in session means the code can no longer work.
  if (name === "NotAuthorizedException" && /session/i.test(message)) return "code";
  if (name === "LimitExceededException" || name === "TooManyRequestsException" || name === "CodeDeliveryFailureException") return "limit";
  return "unknown";
}

function errorName(error: unknown): string | undefined {
  return error instanceof Error ? error.name : undefined;
}
